import { Badge, Tooltip } from '@vnetwork/ui/primitives';
import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import type { OpsServiceRow, OpsServiceStatus, OpsCategory } from '@/api/operations';
import { formatDate } from '@/lib/format';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';

/* ── Status mapping ─────────────────────────────────────────────────────── */

type StatusPillTone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

interface StatusConfig {
  tone: StatusPillTone;
  label: string;
  tooltip: string;
}

const STATUS_CONFIG: Record<OpsServiceStatus, StatusConfig> = {
  active: { tone: 'success', label: 'Active', tooltip: 'Service is running normally' },
  suspended: { tone: 'danger', label: 'Suspended', tooltip: 'Service is suspended' },
  terminated: { tone: 'muted', label: 'Terminated', tooltip: 'Service has been terminated' },
  archived: { tone: 'muted', label: 'Archived', tooltip: 'Service has been archived' },
};

/* ── Category badge tone mapping ─────────────────────────────────────────── */

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const CATEGORY_TONE: Record<OpsCategory, BadgeTone> = {
  cdn: 'info',
  waap: 'warning',
  storage: 'neutral',
  media: 'success',
  compute: 'info',
  registrar: 'neutral',
  other: 'neutral',
};

const CATEGORY_LABEL: Record<OpsCategory, string> = {
  cdn: 'CDN',
  waap: 'WAAP',
  storage: 'Storage',
  media: 'Media',
  compute: 'Compute',
  registrar: 'Registrar',
  other: 'Other',
};

/* ── Column definitions ──────────────────────────────────────────────────── */

/**
 * DataTable column definitions for the Operations services list.
 * StatusPill uses dot + semantic color + Tooltip (no chip in heading — per spec).
 */
export const opsServiceColumns: ColumnDef<OpsServiceRow, unknown>[] = [
  {
    // Service id is an opaque UUID — hidden; the name links to the detail.
    id: 'service_name',
    accessorKey: 'service_name',
    header: 'Service',
    cell: ({ row }) => (
      <TableLink to={`/operations/services/${row.original.id}`} title={row.original.service_name} truncate>
        {row.original.service_name}
      </TableLink>
    ),
  },
  {
    id: 'org',
    accessorKey: 'organization_id',
    header: 'Organization',
    cell: ({ row }) => {
      const { organization_id, customer } = row.original;
      return (
        <EntityLink
          to={`/operations/orgs/${organization_id}`}
          id={organization_id}
          name={customer?.customer_company_name || customer?.customer_name}
        />
      );
    },
  },
  {
    id: 'provider',
    accessorKey: 'provider',
    header: 'Provider',
    cell: ({ row }) => {
      const { provider } = row.original;
      if (!provider) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
      const tone = CATEGORY_TONE[provider.category] ?? 'neutral';
      const catLabel = CATEGORY_LABEL[provider.category] ?? provider.category;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--vn-fg-default)' }}>{provider.name}</span>
          <Badge tone={tone}>{catLabel}</Badge>
        </div>
      );
    },
  },
  {
    id: 'entity_uids',
    accessorKey: 'entity_uids',
    header: 'Entity UIDs',
    cell: ({ row }) => {
      const uids = row.original.entity_uids;
      if (!uids || uids.length === 0) {
        return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
      }
      // Show first UID; if more exist, show count badge.
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-default)' }}>
            {uids[0]}
          </span>
          {uids.length > 1 ? (
            <Tooltip content={uids.slice(1).join(', ')}>
              <Badge tone="neutral">+{uids.length - 1}</Badge>
            </Tooltip>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'domain_count',
    accessorKey: 'domain_count',
    header: 'Domains',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.domain_count}</span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const cfg = STATUS_CONFIG[row.original.status] ?? {
        tone: 'neutral' as StatusPillTone,
        label: row.original.status,
        tooltip: row.original.status,
      };
      return (
        <StatusPill tone={cfg.tone} tooltip={cfg.tooltip}>
          {cfg.label}
        </StatusPill>
      );
    },
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

/** Column ids that can be toggled via Display options. */
export const OPS_TOGGLEABLE_COLUMN_IDS = ['entity_uids', 'domain_count', 'created_at'];
